// =============================================================
// BidProfilesPanel.jsx — one bid profile per bidding company.
//
// The list is the companies someone added — from the Sage entity roster, or
// typed in by hand for a company that is not in Sage. A profile holds what the
// rules filter and the AI screen read. Saving a profile re-screens every open opportunity once against
// the new criteria (the server's signature includes the profiles).
// =============================================================
import { useState } from 'react';
import { B } from '../../constants/brand';
import { api } from '../../api/client';
import { useApi } from '../../hooks/useApi';
import { Btn, Input, Textarea, Select, Card, Badge, Spinner, EmptyState, Modal, ModalFooter, useToast } from '../ui';

const CERT_LABELS = {
  '8a': '8(a)', hubzone: 'HUBZone', sdvosb: 'SDVOSB', vosb: 'VOSB', wosb: 'WOSB', edwosb: 'EDWOSB',
};
const TYPE_LABELS = {
  ffp: 'Firm fixed price', idiq: 'IDIQ', tm: 'Time & materials', cost: 'Cost-reimbursement',
  bpa: 'BPA', grant: 'Grant / cooperative agreement', other: 'Other',
};
const listText = (arr) => (arr || []).join(', ');
const toList = (s) => String(s || '').split(/[,\n]/).map(x => x.trim()).filter(Boolean);

export function BidProfilesPanel() {
  const { toast } = useToast();
  const { data, loading, error, reload } = useApi(() => api.get('govbid/profiles'), []);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);

  if (loading && !data) return <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>;
  if (error) return <Card><div style={{ color: B.red, fontSize: 13 }}>{String(error?.message || error)}</div></Card>;
  if (data && data.available === false) return <Card><div style={{ fontSize: 13, color: B.textMd }}>{data.reason}</div></Card>;
  const profiles = data?.profiles || [];

  const remove = async (p) => {
    if (!window.confirm(`Remove ${p.entityName || p.entityCode} from Bid Profiles? Its profile and recommendations stop showing.`)) return;
    try { await api.del(`govbid/profiles/${p.sageEntityId}`); toast.success('Removed.'); reload(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 280, fontSize: 13, color: B.textMd, maxWidth: 760 }}>
          Add each company that bids, from Sage or by hand, and fill in what it can win. The rules filter uses the registration,
          NAICS, value, deadline and agency fields; the AI screen reads capabilities, preferences and deal-breakers.
          Saving any profile re-screens every open opportunity once.
        </div>
        <Btn size="sm" onClick={() => setAdding(true)}>+ Add company</Btn>
      </div>
      {!profiles.length ? (
        <EmptyState title="No companies yet" message="Add the companies that bid on government work." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {profiles.map(p => (
            <Card key={p.sageEntityId} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: B.text, fontSize: 14 }}>
                  {p.entityName || p.entityCode} <span style={{ color: B.textLt, fontWeight: 400, fontSize: 12 }}>{p.entityCode}</span>
                </div>
                <div style={{ fontSize: 12, color: B.textMd, marginTop: 2 }}>
                  {[p.inSage ? null : 'Not in Sage',
                    p.sizeStatus === 'small' ? 'Small business' : p.sizeStatus === 'other' ? 'Other than small' : null,
                    p.certifications.map(c => CERT_LABELS[c] || c).join(', ') || null,
                    p.naics.length ? `NAICS ${p.naics.map(n => n.code + (n.core ? '' : '*')).join(', ')}` : 'No NAICS listed',
                  ].filter(Boolean).join(' · ')}
                </div>
              </div>
              {p.isBidding ? <Badge color={B.green}>Bidding</Badge> : <Badge color={B.textLt}>Not bidding</Badge>}
              <Btn size="sm" variant="ghost" onClick={() => setEditing(p)}>Edit</Btn>
              <Btn size="sm" variant="ghost" onClick={() => remove(p)}>Remove</Btn>
            </Card>
          ))}
        </div>
      )}
      {adding && (
        <AddCompanyModal sageEntities={data?.sageEntities || []} supportsOutside={!!data?.supportsOutside}
          onClose={() => setAdding(false)}
          onAdded={(profile) => { setAdding(false); reload(); setEditing(profile); }} />
      )}
      {editing && (
        <ProfileModal profile={editing} options={data.options}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
      )}
    </div>
  );
}

// Add one company: pick it from the Sage roster, or type one that is not in Sage.
function AddCompanyModal({ sageEntities, supportsOutside, onClose, onAdded }) {
  const [mode, setMode] = useState(sageEntities.length ? 'sage' : 'outside');
  const [sageId, setSageId] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const add = async () => {
    setBusy(true); setErr(null);
    try {
      const out = await api.post('govbid/profiles', mode === 'sage' ? { sageEntityId: sageId } : { name, code });
      onAdded(out.profile);
    } catch (e) { setErr(e.message || 'Could not add the company.'); }
    finally { setBusy(false); }
  };

  const tab = (key, label, disabled) => (
    <Btn size="sm" variant={mode === key ? 'primary' : 'ghost'} disabled={disabled} onClick={() => { setMode(key); setErr(null); }}>{label}</Btn>
  );
  return (
    <Modal title="Add a company" onClose={onClose} width={560}>
      <div style={{ padding: '4px 24px 8px' }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {tab('sage', 'From Sage', !sageEntities.length)}
          {tab('outside', 'Not in Sage', !supportsOutside)}
        </div>
        {mode === 'sage' ? (
          <Select label="Sage entity" value={sageId} onChange={setSageId}
            options={[{ value: '', label: 'Choose…' }, ...sageEntities.map(e => ({ value: e.sageEntityId, label: `${e.entityName || e.entityCode} (${e.entityCode})` }))]} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Input label="Company name" value={name} onChange={setName} placeholder="e.g. Gulf Coast Environmental, LLC" />
            <Input label="Short code (optional)" value={code} onChange={setCode} placeholder="Made from the name if blank, e.g. GCE" />
            <div style={{ fontSize: 12, color: B.textLt }}>The code is how the AI screen names the company; it must differ from every other company's.</div>
          </div>
        )}
        {!supportsOutside && mode === 'sage' && (
          <div style={{ fontSize: 12, color: B.textLt, marginTop: 8 }}>Adding a company outside Sage needs migration 242.</div>
        )}
        {err && <div style={{ color: B.red, fontSize: 13, marginTop: 10 }}>{err}</div>}
      </div>
      <ModalFooter>
        <Btn variant="ghost" onClick={onClose} disabled={busy}>Cancel</Btn>
        <Btn onClick={add} loading={busy} disabled={busy || (mode === 'sage' ? !sageId : !name.trim())}>Add</Btn>
      </ModalFooter>
    </Modal>
  );
}

function ProfileModal({ profile, options, onClose, onSaved }) {
  const { toast } = useToast();
  const [f, setF] = useState(() => ({
    name: profile.entityName || '', code: profile.entityCode || '',
    isBidding: profile.hasProfile ? profile.isBidding : true,
    uei: profile.uei || '', cage: profile.cage || '', sizeStatus: profile.sizeStatus || '',
    certifications: profile.certifications || [],
    coreNaics: listText(profile.naics.filter(n => n.core).map(n => n.code)),
    stretchNaics: listText(profile.naics.filter(n => !n.core).map(n => n.code)),
    psc: listText(profile.psc),
    minAmount: profile.minAmount ?? '', maxAmount: profile.maxAmount ?? '',
    agencyInclude: listText(profile.agencyInclude), agencyExclude: listText(profile.agencyExclude),
    contractTypes: profile.contractTypes || [], allowRecompete: profile.allowRecompete !== false,
    minLeadDays: profile.minLeadDays ?? '',
    capabilities: profile.capabilities || '', preferences: profile.preferences || '', dealBreakers: profile.dealBreakers || '',
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => { setF(s => ({ ...s, [k]: v })); setErr(null); };
  const toggle = (k, v) => set(k, f[k].includes(v) ? f[k].filter(x => x !== v) : [...f[k], v]);

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await api.put(`govbid/profiles/${profile.sageEntityId}`, {
        ...(profile.inSage === false ? { name: f.name, code: f.code } : {}),
        isBidding: f.isBidding, uei: f.uei, cage: f.cage, sizeStatus: f.sizeStatus || null,
        certifications: f.certifications,
        naics: [...toList(f.coreNaics).map(code => ({ code, core: true })), ...toList(f.stretchNaics).map(code => ({ code, core: false }))],
        psc: toList(f.psc), minAmount: f.minAmount, maxAmount: f.maxAmount,
        agencyInclude: toList(f.agencyInclude), agencyExclude: toList(f.agencyExclude),
        contractTypes: f.contractTypes, allowRecompete: f.allowRecompete, minLeadDays: f.minLeadDays,
        capabilities: f.capabilities, preferences: f.preferences, dealBreakers: f.dealBreakers,
      });
      toast.success('Profile saved. Open opportunities will be re-screened.');
      onSaved();
    } catch (e) {
      setErr(e.message || 'Save failed.');
    } finally { setBusy(false); }
  };

  const check = (label, on, onChange) => (
    <label key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: B.text, marginRight: 14, cursor: 'pointer' }}>
      <input type="checkbox" checked={on} onChange={onChange} /> {label}
    </label>
  );
  const section = (title) => <div style={{ fontWeight: 600, fontSize: 13, color: B.navy, margin: '16px 0 8px' }}>{title}</div>;

  return (
    <Modal title={`Bid profile — ${profile.entityName || profile.entityCode}`} onClose={onClose} width={760}>
      <div style={{ padding: '4px 24px 8px' }}>
        {profile.inSage === false && (
          <div className="harvus-responsive-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 12 }}>
            <Input label="Company name" value={f.name} onChange={v => set('name', v)} />
            <Input label="Short code" value={f.code} onChange={v => set('code', v)} />
          </div>
        )}
        {check('This company bids on government work', f.isBidding, () => set('isBidding', !f.isBidding))}

        {section('Registration')}
        <div className="harvus-responsive-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <Input label="UEI" value={f.uei} onChange={v => set('uei', v)} placeholder="12 characters" />
          <Input label="CAGE code" value={f.cage} onChange={v => set('cage', v)} placeholder="5 characters" />
          <Select label="Size status" value={f.sizeStatus} onChange={v => set('sizeStatus', v)}
            options={[{ value: '', label: 'Not set' }, { value: 'small', label: 'Small business' }, { value: 'other', label: 'Other than small' }]} />
        </div>
        <div style={{ marginTop: 10 }}>
          {(options?.certifications || []).map(c => check(CERT_LABELS[c] || c, f.certifications.includes(c), () => toggle('certifications', c)))}
        </div>

        {section('What it bids on (the rules filter)')}
        <div className="harvus-responsive-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Input label="Core NAICS codes" value={f.coreNaics} onChange={v => set('coreNaics', v)} placeholder="541513, 541512" />
          <Input label="Stretch NAICS codes" value={f.stretchNaics} onChange={v => set('stretchNaics', v)} placeholder="Will bid, weaker fit" />
          <Input label="PSC codes" value={f.psc} onChange={v => set('psc', v)} placeholder="D310, R425" />
          <Input label="Minimum lead time (days)" value={String(f.minLeadDays)} onChange={v => set('minLeadDays', v)} placeholder="e.g. 14" />
          <Input label="Minimum contract value ($)" value={String(f.minAmount)} onChange={v => set('minAmount', v)} placeholder="250,000" />
          <Input label="Maximum contract value ($)" value={String(f.maxAmount)} onChange={v => set('maxAmount', v)} placeholder="10,000,000" />
          <Input label="Agencies to target (blank = any)" value={f.agencyInclude} onChange={v => set('agencyInclude', v)} placeholder="Veterans Affairs, Army Corps" />
          <Input label="Agencies to exclude" value={f.agencyExclude} onChange={v => set('agencyExclude', v)} placeholder="Comma-separated" />
        </div>
        <div style={{ fontSize: 12, color: B.textLt, marginTop: 6 }}>
          A field the notice does not state never rules a company out — it is only checked when the notice says it.
        </div>

        {section('How it bids (read by the AI screen)')}
        <div style={{ marginBottom: 8 }}>
          {(options?.contractTypes || []).map(t => check(TYPE_LABELS[t] || t, f.contractTypes.includes(t), () => toggle('contractTypes', t)))}
        </div>
        {check('Will bid against an incumbent (recompetes)', f.allowRecompete, () => set('allowRecompete', !f.allowRecompete))}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
          <Textarea label="Capabilities" rows={3} value={f.capabilities} onChange={v => set('capabilities', v)}
            placeholder="What the company does, for whom, and where. Two or three sentences." />
          <Textarea label="Preferences" rows={2} value={f.preferences} onChange={v => set('preferences', v)}
            placeholder="Locations, contract sizes or agencies you prefer, without ruling the rest out." />
          <Textarea label="Deal-breakers" rows={2} value={f.dealBreakers} onChange={v => set('dealBreakers', v)}
            placeholder="e.g. requires a facility clearance; bonding over $5M; on-site work outside the Gulf South." />
        </div>
        {err && <div style={{ color: B.red, fontSize: 13, marginTop: 12 }}>{err}</div>}
      </div>
      <ModalFooter>
        <Btn variant="ghost" onClick={onClose} disabled={busy}>Cancel</Btn>
        <Btn onClick={save} loading={busy} disabled={busy}>Save profile</Btn>
      </ModalFooter>
    </Modal>
  );
}
